import { useState, useCallback } from "react"
import { useQuery, useSubscription, useMutation } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { toast } from "sonner"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ProgressRing } from "@/components/shared/progress-ring"
import { Download, RefreshCw, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  OutboundDeliveriesQuery,
  EndpointsQuery,
  ApplicationsQuery,
  OutboundDeliveryUpdatedSubscription,
  BulkRetryOutboundDeliveriesMutation,
} from "@/lib/graphql/outbound"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"
import type { DeliveryStatus } from "@/lib/generated/graphql"

type OutboundDeliveryItem = ResultOf<typeof OutboundDeliveriesQuery>["outboundDeliveries"]["nodes"][number]

const LIMIT = 25

const columns: Column<OutboundDeliveryItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.message.eventType}</code>,
  },
  {
    header: "Endpoint",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-50 truncate block">
        {row.endpoint.url}
      </code>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <ProgressRing value={row.attempts} max={row.maxAttempts} size={28} strokeWidth={2.5} />
    ),
  },
  {
    header: "Next Attempt",
    cell: (row) =>
      row.nextAttemptAt ? <TimeAgo date={row.nextAttemptAt} /> : (
        <span className="text-xs text-muted-foreground">-</span>
      ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function OutboundDeliveriesPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const [bulkRetryOpen, setBulkRetryOpen] = useState(false)

  const status = (searchParams.get("status") as DeliveryStatus) || undefined
  const endpointId = searchParams.get("endpointId") ?? undefined
  const applicationId = searchParams.get("applicationId") ?? undefined
  const messageId = searchParams.get("messageId") ?? undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [status, endpointId, applicationId, messageId, search])

  const [endpoints] = useQuery({ query: EndpointsQuery })
  const [applications] = useQuery({ query: ApplicationsQuery })
  const [result, reexecute] = useQuery({
    query: OutboundDeliveriesQuery,
    variables: { status, endpointId, applicationId, messageId, search, after: pagination.after, first: pagination.first },
  })

  const handleDeliveryUpdate = useCallback(
    (
      _prev: ResultOf<typeof OutboundDeliveryUpdatedSubscription> | undefined,
      response: ResultOf<typeof OutboundDeliveryUpdatedSubscription>,
    ) => {
      if (response?.outboundDeliveryUpdated) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: OutboundDeliveryUpdatedSubscription, pause: !isConfigured }, handleDeliveryUpdate)

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Outbound Deliveries" description="Outbound webhook delivery attempts">
        <Button variant="outline" size="sm" onClick={() => setBulkRetryOpen(true)}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Bulk Retry
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.outboundDeliveries.nodes ?? []).map((d) => ({
                id: d.id, eventType: d.message.eventType, endpoint: d.endpoint.url,
                status: d.status, attempts: d.attempts, maxAttempts: d.maxAttempts,
                nextAttemptAt: d.nextAttemptAt, createdAt: d.createdAt,
              }))
              exportJSON(rows, "outbound-deliveries")
            }}>Export JSON</DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.outboundDeliveries.nodes ?? []).map((d) => ({
                id: d.id, eventType: d.message.eventType, endpoint: d.endpoint.url,
                status: d.status, attempts: d.attempts, maxAttempts: d.maxAttempts,
                nextAttemptAt: d.nextAttemptAt ?? "", createdAt: d.createdAt,
              }))
              exportCSV(rows, ["id", "eventType", "endpoint", "status", "attempts", "maxAttempts", "nextAttemptAt", "createdAt"], "outbound-deliveries")
            }}>Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={applicationId ?? "all"}
          onValueChange={(v) => setFilter("applicationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All apps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All apps</SelectItem>
            {applications.data?.applications.nodes.map((app) => (
              <SelectItem key={app.id} value={app.id}>
                {app.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={endpointId ?? "all"}
          onValueChange={(v) => setFilter("endpointId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All endpoints" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All endpoints</SelectItem>
            {endpoints.data?.endpoints.nodes.map((ep) => (
              <SelectItem key={ep.id} value={ep.id}>
                {ep.url}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search deliveries..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />

        {messageId && (
          <Badge variant="secondary" className="gap-1 text-xs">
            Message: {messageId.slice(0, 8)}...
            <button type="button" onClick={() => setFilter("messageId", "")} className="ml-0.5 hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>

      <DataTable
        columns={columns}
        data={result.data?.outboundDeliveries.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.outboundDeliveries.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No outbound deliveries"
        onRowClick={(row) => navigate(`/outbound-deliveries/${row.id}`)}
      />

      <OutboundBulkRetryDialog
        open={bulkRetryOpen}
        onOpenChange={setBulkRetryOpen}
        endpoints={endpoints.data?.endpoints.nodes ?? []}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />
    </div>
  )
}

type EndpointOption = ResultOf<typeof EndpointsQuery>["endpoints"]["nodes"][number]

function OutboundBulkRetryDialog({
  open,
  onOpenChange,
  endpoints,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  endpoints: EndpointOption[]
  onSuccess: () => void
}) {
  const [, bulkRetry] = useMutation(BulkRetryOutboundDeliveriesMutation)
  const [since, setSince] = useState("")
  const [until, setUntil] = useState("")
  const [endpointId, setEndpointId] = useState("all")
  const [status, setStatus] = useState<DeliveryStatus>("FAILED")
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const res = await bulkRetry({
      filter: {
        since: since || undefined,
        until: until || undefined,
        endpointId: endpointId === "all" ? undefined : endpointId,
        status: status || undefined,
      },
    })
    setSubmitting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success(`Retried ${res.data?.bulkRetryOutboundDeliveries.count ?? 0} deliveries`)
    onOpenChange(false)
    setSince("")
    setUntil("")
    setEndpointId("all")
    setStatus("FAILED")
    onSuccess()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk Retry Outbound Deliveries</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ob-bulk-since">Since</Label>
            <Input
              id="ob-bulk-since"
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ob-bulk-until">Until</Label>
            <Input
              id="ob-bulk-until"
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Endpoint</Label>
            <Select value={endpointId} onValueChange={setEndpointId}>
              <SelectTrigger>
                <SelectValue placeholder="All endpoints" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All endpoints</SelectItem>
                {endpoints.map((ep) => (
                  <SelectItem key={ep.id} value={ep.id}>
                    {ep.url}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as DeliveryStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Retrying..." : "Retry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
